import React, { useCallback } from 'react';
import { Typography, CardContent, Stack, CircularProgress, Box, Switch, Grid } from '@mui/material';
import { Help } from '@mui/icons-material';
import TopFiveProjects from '../components/RecentlyUploadedProjects';
import { useAuth } from 'contexts/AuthContext';
import ProjectListMap from '../components/map/ProjectListMap';
import { useQuery } from 'react-query';
import dataKeys from '../common/react-query/data-keys';
import { fetchProjects } from '../components/project/project.api';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { NAVBAR_HEIGHT } from '../common/constants/general';
import IconTooltip from '../components/common/IconTooltip';
import { buildSearchRequestBody } from '../common/utils/azure-search.util';

const DEFAULT_PAGE_SIZE = 25;

const Home: React.FC = () => {
  const { user } = useAuth();
  const [fileUploadDialogOpen, setFileUploadDialogOpen] = React.useState(false);
  const closeFileUploadDialog = () => setFileUploadDialogOpen(false);
  const initialPageSize = DEFAULT_PAGE_SIZE;
  const [filterString, setFilterString] = React.useState<any[]>([]);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });
  const [sortingModel, setSortingModel] = React.useState<GridSortModel>([{ field: 'CreatedOn', sort: 'desc' }]);
  const [initialDataLoaded, setInitialDataLoaded] = React.useState(true);

  const fetchProjectsWrapper = useCallback(
    async (filterString: any, ignorePagination: boolean) => {
      try {
        const searchRequestBody = buildSearchRequestBody('', filterString, paginationModel, sortingModel, ignorePagination);
        const response = await fetchProjects(searchRequestBody);
        return response ?? [];
      } catch (error) {
        console.error('Error fetching projects:', error);
        return [];
      }
    },
    [paginationModel, sortingModel],
  );

  const { data, isLoading, isFetching, isError, error } = useQuery(
    [dataKeys.allProjects, filterString, paginationModel, sortingModel],
    () => fetchProjectsWrapper(filterString, true),
    {
      onSettled: () => setInitialDataLoaded(false),
    },
  );

  let projects: any[] = [];
  let total = 0;

  if (data?.error) {
    toast.error(data.error.message);
  } else if (data?.value && Array.isArray(data.value)) {
    projects = data.value;
    total = data['@odata.count'] || 0;
  }

  if (isError) {
    return (
      <Grid container justifyContent='center' spacing={0}>
        <Typography color='error'>Failed to load projects</Typography>
      </Grid>
    );
  }
  const toggleMyProjectsFilter = () => {
    const userId = user?.id ?? '';
    const myProjectsFilter = `ObserverId eq '${userId}'`;

    let newFilterArray = [...filterString]; // Assuming filterArray is your state holding the array of filters

    if (myProjectsFilterOn) {
      // Remove the myProjects filter from the array
      newFilterArray = newFilterArray.filter(filter => filter !== myProjectsFilter);
    } else {
      // Add the myProjects filter to the array
      newFilterArray.push(myProjectsFilter);
    }

    setFilterString(newFilterArray); // Update the state with the new array of filters
  };
  const myProjectsFilterOn = React.useMemo(() => {
    const userId = user?.id ?? '';
    const myProjectsFilter = `ObserverId eq '${userId}'`;

    if (Array.isArray(filterString)) {
      return filterString.some(filter => filter === myProjectsFilter);
    }

    return false;
  }, [filterString, user?.id]);

  return (
    <Box sx={{ height: `calc(100vh - ${NAVBAR_HEIGHT})`, display: 'flex', flexDirection: 'column', padding: 1 }}>
      {/* Main Content */}
      <Grid container spacing={3} sx={{ flexGrow: 1, height: '100%', maxHeight: '100%' }}>
        {/* Left Column: My Projects */}
        <Grid size={{ xs: 12, md: 4 }}>
          <CardContent sx={{ padding: 0, height: '100%', maxHeight: '100%' }}>
            <TopFiveProjects user={user} />
          </CardContent>
        </Grid>

        {/* Right Column: Map */}
        <Grid
          size={{ xs: 12, md: 8 }}
          component='div'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 3,
            height: '100%',
            maxHeight: '100%',
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              background: 'white',
              padding: 2,
              boxShadow: 1,
              height: '100%',
              maxHeight: '100%',
            }}
          >
            <Stack direction='row' alignItems='center' pb={2}>
              <Switch checked={myProjectsFilterOn} onChange={toggleMyProjectsFilter} />
              <Typography variant='body2' fontWeight={'bold'}>
                My Projects Only
              </Typography>
              <IconTooltip icon={<Help />} placement='right' title='Toggle to view your projects only.' />
            </Stack>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: `calc(100% - 40px)` }}>
              {isLoading ? (
                <CircularProgress />
              ) : isError ? (
                <Typography color='error' textAlign='center'>
                  Failed to load projects
                </Typography>
              ) : projects.length === 0 ? (
                <Typography textAlign='center'>No projects available.</Typography>
              ) : (
                <ProjectListMap projects={projects} loading={isLoading} listOptions={{ hideList: true }} zoomLevel={4} />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
